import { Widget, Product } from "@/types";

export interface APIResponse {
  error_code: number;
  text: string;
  data: {
    widgets: Widget[];
    response_text: string;
    question: string;
    model: string | null;
    images?: string[];
    products?: Product[];
  };
}

export interface AIResponse {
  id?: string;
  message: string;
  isQuestion?: boolean;
  options?: string[];
  widgets?: Widget[];
  error_code?: number;
  text?: string;
  question?: string;
  model?: string | null;
  modelInfo?: {
    modelName?: string;
    version?: string;
    input?: any;
    webhookUrl?: string;
  };
  images?: string[];
  products?: Product[];
  productIds?: string[]; // 🆕 VectorSearch'ten gelen product ID'leri
  offerMap?: Record<string, string>;
  task?: string; // Yeni alan (örn: "CHATTING" veya widget tetikleyici task)
  output?: any[]; // Widget output data
  // CREATIVE_GENERATION için backend'den gelen filtrelenmiş modeller
  creativeModels?: EachalabModel[];
}

export interface APIRequestParams {
  // Yeni /chat endpoint'i için
  message?: string; // Yeni alan
  stream?: boolean; // Yeni alan (default false)
  model?: string;
  chat_id?: string; // Opsiyonel - creative generation için

  // Geriye dönük uyumluluk için (eski kullanım)
  prompt?: string;
  user_id?: string;
  type?: string;
  message_id?: string;
}

// Conversations API Types
export interface User {
  _id: string;
  name: string;
  image:
    | {
        type?: string;
        size?: number;
        folder?: string;
        fileUrl?: string;
      }
    | string
    | null;
  email: string;
  password?: string;
  isVerified: boolean;
  verificationCode?: string | null;
  verificationCodeExpiresAt?: string | null;
  lastVerificationSentAt?: string | null;
  role: string | object;
  token: number;
  tokens?: {
    subscription: number;
    oneTime: number;
  };
  maxTokens?: {
    subscription: number;
    oneTime: number;
  };
  theme: string | object;
  language: string | object;
  isDeleted: boolean;
  provider?: string;
  region?: string;
  zone?: string;
  currency?: string;
  height?: number;
  size?: string;
  shoeSize?: number;
  gender?: string | object;
  age?: number;
  addresses: Array<{
    label?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    _id?: string;
  }>;
  phoneNumber?: string;
  personaSetupDismissedAt?: string | null;
  gate?: {
    needsPersonaSetup: boolean;
    needsOnboarding: boolean;
  };
  stripeCustomerId?: string;
  identificationNumber?: string;
  notifications?: NotificationSettings;
  agreementConfirmations?: AgreementConfirmation[];
  createdAt: string;
  updatedAt: string;
}

// Profile Update Types
export interface UpdateProfileRequest {
  name?: string;
  region?: string;
  language?: object;
  gender?: string | object;
  age?: number;
  height?: number;
  size?: string;
  shoeSize?: number;
  photo?: string;
  omuzUsername?: string;
  phoneNumber?: string;
  personaSetupDismissedAt?: string;
  identificationNumber?: string;
  role?: string;
  theme?: object;
}

export interface UpdateProfileResponse {
  data: User;
  statusCode: number;
  success: boolean;
  message: string;
}

// 👗 Try-On Photos API Types
export interface TryOnPhoto {
  /** Subdocument id — silme/güncelleme işlemlerinde kullanılan handle */
  id: string;
  /** Kullanıcının bu fotoğrafa verdiği isim ("Önden", "eşim" vb.) */
  name: string;
  /** Hazır tam URL */
  url: string;
  /** Ana fotoğraf — hesap sahibinin kendisi */
  isSelf: boolean;
  gender?: string;
  height?: number;
  size?: string;
}

export interface TryOnPhotosData {
  photos: TryOnPhoto[];
  /** Toplamda tutulabilecek fotoğraf sayısı */
  limit: number;
}

export interface TryOnPhotosResponse {
  data: TryOnPhotosData;
  statusCode: number;
  success: boolean;
}

export interface TryOnPhotoResponse {
  data: TryOnPhoto;
  statusCode: number;
  success: boolean;
}

export interface AddTryOnPhotoRequest {
  file: File;
  name?: string;
}

export interface UpdateTryOnPhotoRequest {
  name?: string;
  isSelf?: boolean;
  gender?: string;
  height?: number;
  size?: string;
}

// 🔐 Change Password API Types
export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  data: {};
  statusCode: number;
  success: boolean;
  message: string;
}

export interface Conversation {
  _id: string;
  platform: string;
  name: string;
  // Backend bazı endpoint'lerde userId'yi string olarak döndürebiliyor
  userId: User | string;
  chat: string[];
  isPublic?: boolean; // Share özelliği için - backend'den gelebilir
  shareToken?: string | null; // Public sohbetler için paylaşım token'ı
  /**
   * Konuşmanın açıldığı modül (`fashion`, `models`…). Sunucuda saklanır;
   * boş/yok = genel asistan. Tek kaynak burasıdır, istemci haritası yoktur.
   */
  module?: string | null;
  /** Modüle hizmet eden ajan (`commerce`, `models`). Yalnızca raporlama. */
  agentId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationsListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sort?: string;
  search?: string; // API search parametresi
  searchTerm?: string; // Deprecated: Geriye dönük uyumluluk için (search kullanılmalı)
  /**
   * Yalnızca bu modülün sohbetleri. Verilmezse hub: tüm modüller + genel
   * asistan. Kapalı küme; bilinmeyen değeri backend 400 ile reddeder.
   */
  module?: string;
}

export interface ConversationsMeta {
  page: number;
  limit: number;
  total: number;
  totalPage?: number; // Deprecated: API totalPages kullanıyor
  totalPages: number; // API'den gelen gerçek field
}

export interface ConversationsListResponse {
  success: boolean;
  message: string;
  data: {
    result: Conversation[];
    meta: ConversationsMeta;
  };
}

// Change conversation name types
export interface ChangeConversationNameRequest {
  name: string;
}

export interface ChangeConversationNameResponse {
  success: boolean;
  message: string;
  data: {
    conversation: Conversation;
  };
}

// Conversation messages types - Backend format
export interface BackendChatMessage {
  _id: string;
  userId: User;
  model: string | null;
  price: number;
  chatId: string;
  task?: string;
  prompt: BackendMessageContent[];
  response: BackendMessageContent[];
  modelInfo: {
    input: any;
    eachlabs_models?: any;
    modelName?: string;
  };
  isPublic?: boolean;
  feedback?: 0 | 1 | 2 | null;
  runtime?: number; // Response süresi
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface BackendMessageAttachment {
  url: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

/** Open-web product result (not Harmony catalog) */
export interface WebSearchProduct {
  externalId: string;
  title: string;
  price: number | null;
  currency: string;
  imageUrl: string;
  productUrl: string;
  source: string;
  rating: number | null;
  ratingCount: number | null;
  position: number;
}

/** Bir turun sunduğu uygulama dışı bağlantı (kargo takip, fatura, satıcı sayfası). */
export interface BackendExternalLink {
  url: string;
  label: string;
}

export interface BackendMessageContent {
  // 🆕 vectorSearch / webSearch / externalLink tipleri de backend'den gelebiliyor
  type:
    | "text"
    | "card"
    | "library"
    | "vectorSearch"
    | "webSearch"
    | "externalLink"
    | "productComparison";
  content?: string;
  isCard: boolean;
  cardContent: BackendCardContent[];
  library?: LibraryItem | null;
  attachments?: BackendMessageAttachment[];
  // 🆕 VectorSearch response yapısı
  vectorSearch?: {
    products?: string[];
    offerMap?: Record<string, string>;
    resultGroups?: Array<{
      label: string;
      discoverQuery?: string;
      recipient: "self" | "other" | "unknown";
      productIds: string[];
      offerMap?: Record<string, string>;
      /** Katalogda karşılığı çıkmayan istek için web kartları. */
      webProducts?: WebSearchProduct[];
      webQuery?: string;
      filterOptions?: {
        categories?: Array<{ id: string; name: string }>;
        brand?: Array<{ id: string; name: string }>;
        attributes?: Array<{
          id: string;
          name: string;
          value: string;
          attributeId?: string;
        }>;
        [key: string]:
          | Array<{
              id: string;
              name: string;
              value?: string;
              attributeId?: string;
            }>
          | string[]
          | undefined;
      };
    }>;
    filterOptions?: {
      categories?: Array<{ id: string; name: string }>;
      brand?: Array<{ id: string; name: string }>;
      attributes?: Array<{
        id: string;
        name: string;
        value: string;
        attributeId?: string;
      }>;
      [key: string]:
        | Array<{
            id: string;
            name: string;
            value?: string;
            attributeId?: string;
          }>
        | string[]
        | undefined;
    };
    message?: string;
    filters?: {
      category?: string | null;
      brand?: string | null;
      price?: {
        min?: number | null;
        max?: number | null;
      };
    };
  } | null;
  // 🆕 Web search product cards (catalog dışında bulunan ürünler)
  webSearch?: {
    query?: string;
    products?: WebSearchProduct[];
  } | null;
  // 🆕 Turun sunduğu uygulama dışı bağlantılar. Stream sırasında `external_link`
  // event'iyle geliyor; backend ayrıca kalıcı olarak kaydediyor, çünkü cevabın
  // metni bağlantıdan söz ediyor ve sayfa yenilendiğinde kaybolmamalı.
  externalLinks?: BackendExternalLink[] | null;
  productComparison?: {
    products?: string[];
    max?: number;
  } | null;
}

export interface BackendCardContent {
  title: string;
  description: string;
  type: string;
  file: string;
  price: number;
}

export interface BackendConversationDetail {
  _id: string;
  platform: string;
  name: string;
  userId: string;
  chat: BackendChatMessage[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface BackendConversationMessagesResponse {
  success: boolean;
  message: string;
  data: BackendConversationDetail;
}

// Infinite scroll için yeni response tipi
export interface BackendInfiniteScrollResponse {
  success: boolean;
  message: string;
  data: {
    messages: BackendChatMessage[];
    total: number;
    hasMore: boolean;
    currentPage: number;
    isPublic: boolean;
    canEdit: boolean;
  };
}

// Message feedback types
export interface MessageFeedbackRequest {
  messageId: string;
  feedback: 0 | 1 | 2;
}

export interface MessageFeedbackResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: BackendChatMessage;
}

// Legacy types - UI compatibility için korunuyor
export interface ChatMessage {
  _id: string;
  userId: User;
  model: string | null;
  price: number;
  chatId: string;
  prompt: MessageContent[];
  response: MessageContent[];
  modelInfo: {
    input: any;
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface MessageContent {
  type: "text" | "image" | "card";
  content?: string;
  isCard: boolean;
  cardContent: CardContent[];
}

export interface CardContent {
  title: string;
  description: string;
  type: string;
  file: string;
  price: number;
}

export interface ConversationDetail {
  _id: string;
  platform: string;
  name: string;
  userId: string;
  chat: ChatMessage[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface ConversationMessagesResponse {
  success: boolean;
  message: string;
  data: ConversationDetail;
}

// LLM Provider Types

/**
 * request_schema.input alanlarındaki çok dilli metin (`title_i18n` /
 * `description_i18n`). Tüm diller opsiyoneldir: backend yalnızca kaynak metni
 * dolu olan alanlar için çeviri üretir.
 */
export type LocalizedInputText = Partial<
  Record<"en" | "tr" | "de" | "es" | "fr" | "ar" | "it", string>
>;

// Multi-language description support
export interface LLMModelDescription {
  en: string;
  tr: string;
  de: string;
  es: string;
  fr: string;
  it?: string;
  ar?: string;
}

// Provider reference within model
export interface LLMProviderReference {
  _id: string;
  name: string;
  isDeleted: boolean;
  models: string[]; // Array of model IDs
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

// Provider object (detailed)
export interface EachalabProvider {
  _id: string;
  name: string;
  models: string[];
  provider_type: string;
  base_url: string;
  endpoint_path: string;
  http_method: string;
  isDeleted: boolean;
  priority: number;
  timeout: number;
  auth_type: string;
  auth_header_key: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

// Brand object
export interface EachalabBrand {
  _id: string;
  title: string;
  __v: number;
  thumbnail: {
    type: string;
    size: number;
    folder: string;
    fileUrl: string;
  };
}

// Category object
export interface EachalabCategory {
  _id: string;
  icon: {
    type: string;
    family: string;
    name: string;
  };
  label: Record<string, string>;
}

// Subcategory object
export interface EachalabSubcategory {
  _id: string;
  label: Record<string, string>;
}

// Attribute object
export interface EachalabAttribute {
  _id: string;
  name: string;
  label: Record<string, string>;
}

// Readme object
export interface EachalabReadme {
  overview: Record<string, string>;
  what_can_i_use_for: Record<string, string>;
  key_considerations: Record<string, string>;
  technical_spec: Record<string, string>;
}

// Yeni backend şemasına uygun fiyat yapısı
export interface LLMModelPricing {
  inputTokenPrice: number;
  outputTokenPrice: number;
  currency: string;
}

// Yeni backend şemasına uygun LLM Model
export interface LLMModel {
  _id: string;
  name: string; // model kimliği
  title?: string; // görünen ad (opsiyonel olabilir)
  thumbnail?: {
    type: string;
    size: number;
    folder: string;
    fileUrl: string;
  };
  thumbnail_url?: string;
  pricing?: LLMModelPricing;
  description: LLMModelDescription;
  provider: LLMProviderRaw | null; // backend artık provider nesnesini veya null döndürüyor
  provider_type?: string;
  supported_tasks?: string[];
  supported_subtasks?: string[];
  complexity_level?: number;
  isDeleted: boolean;
  max_tokens?: number;
  context_window?: number;
  supports_streaming?: boolean;
  avg_response_time_ms?: number;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
  slug?: string;
  __v?: number;
  // Legacy destek (varsa)
  price?: number;
}

// Pricing Plan Types
export interface PricingFeature {
  _id: string;
  text: {
    tr: string;
    en: string;
    de: string;
    es: string;
    fr: string;
    it?: string;
  };
  icon: string;
  isHighlighted: boolean;
}

export interface PricingPlan {
  _id: string;
  name: string;
  planType: "recurring" | "topup";
  billingCycle: "monthly" | "yearly" | "one-time";
  originalPrice: number;
  discountedPrice: number;
  discountPercentage: number;
  creditCost: number;
  tokenAmount: number;
  bonusCredits: number;
  icon: string;
  isFeatured: boolean;
  isPopular: boolean;
  features?: PricingFeature[];
  isActive: boolean;
  isFree?: boolean;
  sortOrder: number;
  stripePriceId?: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
  currency?: string;
  usdPrices?: {
    originalPrice: number;
    discountedPrice: number;
    currency: string;
  };
  offerBadge?: {
    title: string;
    description: string;
  };
}

export interface PricingResponse {
  success: boolean;
  message: string;
  data: {
    result: PricingPlan[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPage: number;
    };
  };
}

// DEPRECATED: Use getAllLLMModels instead
export interface LLMProvider {
  _id: string;
  name: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  models: LLMModel[];
}

// API Response Types
export interface LLMModelsResponse {
  success: boolean;
  message: string;
  data: {
    result: LLMModel[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPage: number;
    };
  };
}

// 🔄 Yeni backend şeması (ham) için tipler
export interface LLMProviderRaw {
  _id: string;
  name: string;
  provider_type: string;
  base_url: string;
  endpoint_path: string;
  http_method: string;
  status: string;
  priority: number;
  timeout: number;
  headers: Record<string, any>;
  auth_type: string;
  auth_header_key: string;
  models: string[];
  createdAt: string;
  updatedAt: string;
}

export interface LLMModelRawPricing {
  // Eski tip geriye dönük destek için bırakıldı
  input_price_per_unit: number;
  output_price_per_unit: number;
  unit_type: string; // e.g. "token"
  currency: string; // e.g. "USD"
}

export interface LLMModelRaw {
  // Eski backend şemasına geriye dönük destek (kullanımda değil)
  pricing: LLMModelRawPricing;
  _id: string;
  model_id: string;
  name: string;
  provider_id: LLMProviderRaw;
  provider_type: string;
  supported_tasks: string[];
  supported_subtasks: string[];
  complexity_level: number;
  status: string;
  max_tokens: number;
  context_window: number;
  supports_streaming: boolean;
  avg_response_time_ms: number;
  thumbnail_url?: string;
  description: LLMModelDescription[];
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, any>;
  slug?: string;
}

export interface LLMModelsRawResponse {
  // Eski ad, yeni şema ile aynı zarf yapısını dönecek şekilde korundu
  success: boolean;
  message: string;
  data: {
    result: LLMModel[]; // Artık doğrudan yeni backend model listesi
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPage: number;
    };
  };
}

export interface LLMProvidersMeta {
  page: number;
  limit: number;
  total: number;
  totalPage: number;
}

export interface LLMProvidersResponse {
  success: boolean;
  message: string;
  data: {
    result: LLMProvider[];
    meta: LLMProvidersMeta;
  };
}

// Eachalab Model Types
/**
 * `request_schema.input` içindeki tek bir alan tanımı (backend'in provider
 * sync'i yazar). Nesne alanları (`type: "object"`) kendi alt alanlarını
 * `properties` içinde, nesne listeleri ise TEK bir öğenin şeklini `items`
 * içinde aynı tanım biçimiyle taşır.
 */
export interface ModelInputDescriptor {
  name: string;
  type: string;
  title: string;
  component: string;
  order: number;
  basic_mode: boolean;
  description: string | false;
  default: string | boolean | number;
  minimum: number;
  maximum: number;
  required: boolean;
  flow_type: string;
  options: string | boolean;
  accepted_extensions: string[];
  array_items_json?: string;
  // Provider sync tarafından yazılan çeviriler. İngilizce `title` /
  // `description` yerine geçmez, yanlarına eklenir; yalnızca GÖSTERİM
  // içindir — generation payload'ı her zaman `name` ile kurulur.
  title_i18n?: LocalizedInputText;
  description_i18n?: LocalizedInputText;
  /** `type: "object"`: alt alanlar, ada göre. */
  properties?: Record<string, ModelInputDescriptor>;
  /** Nesne listesi (`type: "array"`): tek bir öğenin şekli. */
  items?: ModelInputDescriptor;
}

export interface EachalabModel {
  _id: string;
  title: string;
  name: string;
  // Provider can be string or object
  provider: string | EachalabProvider;
  provider_type: string;
  supported_tasks: string[];
  supported_subtasks: string[];
  complexity_level: number;
  isDeleted: boolean;
  max_tokens: number;
  context_window: number;
  supports_streaming: boolean;

  // Thumbnail - can be object or url string
  thumbnail?: {
    type?: string;
    size?: number;
    folder?: string;
    fileUrl?: string;
  };
  thumbnail_url?: string;

  // Brand object
  brand: EachalabBrand;

  // Request schema
  request_schema: {
    model: string;
    version: string;
    input: Record<string, ModelInputDescriptor>;
    webhook_url: string;
  };

  // Response schema
  response_schema: {
    output_example: {
      name: string;
      input: Record<string, any>;
      output: string | string[]; // Can be string or array
      inference_time: number;
      total_time: number;
    };
  };

  // Pricing
  pricing: {
    inputTokenPrice: number;
    outputTokenPrice: number | null;
    currency: string;
    type?: "static" | "dynamic";
    charges?: {
      labels: Record<
        string,
        {
          en: string;
          tr: string;
          de: string;
          fr: string;
          it?: string;
          es: string;
          ar?: string;
        }
      >;
      values: Array<Record<string, string | number>>;
      _id: string;
    };
    _id?: string;
  };

  avg_response_time_ms: number;
  description: LLMModelDescription;

  // Optional new fields
  attributes?: EachalabAttribute[];
  categories?: EachalabCategory[];
  subcategories?: EachalabSubcategory[];
  readme?: EachalabReadme;
  status?: string;
  average_rating?: number;
  metadata?: Record<string, any>;
  output_object?: string;

  // Social metrics (static for now, will be dynamic later)
  likeCount?: string;
  rating?: number;

  // ✅ New social metrics from API
  likesCount?: number;
  commentsCount?: number;
  sharedCount?: number;

  createdAt: string;
  updatedAt: string;
  __v?: number;
}
export interface EachalabModelsListParams {
  page?: number;
  limit?: number;
  sort?: string;
}

export interface EachalabModelsMeta {
  page: number;
  limit: number;
  total: number;
  totalPage: number;
}

export interface EachalabModelsResponse {
  success: boolean;
  message: string;
  data: {
    result: EachalabModel[];
    meta: EachalabModelsMeta;
  };
}

// Create Conversation Types
export interface CreateConversationRequest {
  platform: string;
  initialMessage?: string; // Backend'de conversation ismini belirlemek için kullanılır
  isTemporary?: boolean; // Temporary chat için flag
  /** Konuşmanın açıldığı modül (`fashion`, `models`…); genel asistanda boş. */
  module?: string;
  /**
   * Modülün ajanı (`commerce`, `models`). Backend `CreateConversationDto`
   * bu alanı henüz kabul etmiyor (whitelist sessizce düşürür); eklediği gün
   * istemcide değişiklik gerekmez.
   */
  agentId?: string;
}

export interface CreateConversationData {
  platform: string;
  name: string;
  userId: string;
  chat: string[];
  _id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface CreateConversationResponse {
  success: boolean;
  message: string;
  data: CreateConversationData;
}

// Delete Conversation Types
export interface DeleteConversationResponse {
  success: boolean;
  message: string;
  data?: any; // Backend'den dönen ek data varsa
}

// Address Types
export interface Address {
  _id: string;
  label: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
  isDefault?: boolean;
}

export interface GetAddressesResponse {
  success: boolean;
  message: string;
  data: Address[];
}

export interface AddAddressPayload {
  label: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
  isDefault?: boolean;
}

export interface AddAddressResponse {
  success: boolean;
  message: string;
  data: Address;
}

export interface UpdateAddressPayload {
  label?: string;
  firstName?: string;
  lastName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  isDefault?: boolean;
}

export interface UpdateAddressResponse {
  success: boolean;
  message: string;
  data: Address;
}

export interface DeleteAddressPayload {
  isDeleted: boolean;
}

export interface DeleteAddressResponse {
  success: boolean;
  message: string;
  data?: {
    _id: string;
    isDeleted: boolean;
  };
}

// Location Types
export interface Country {
  _id: string;
  countryId: number;
  name: string;
  globalName: string;
}

export interface City {
  _id: string;
  cityId: number;
  name: string;
}

export interface Town {
  _id: string;
  townId: number;
  name: string;
}

export interface Quarter {
  _id: string;
  name: string;
}

export interface GetCountriesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: Country[];
}

export interface GetCitiesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: City[];
}

export interface GetTownsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: Town[];
}

export interface GetQuartersResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: Quarter[];
}

export interface LocationParams {
  type: "country" | "cities" | "towns" | "quarters";
  countryId?: number;
  cityId?: number;
  townId?: number;
}

// Subscription Types
/**
 * Checkout oturumunu açan tarayıcının reklam kimliği. Stripe webhook'u
 * tarayıcısız çalıştığı için bu değerler oturumla birlikte taşınmazsa bir
 * ödeme, onu üreten reklam tıklamasına bağlanamıyor.
 */
export interface CheckoutAttribution {
  gaClientId?: string;
  gaSessionId?: string;
  gclid?: string;
  wbraid?: string;
}

export interface CreateCheckoutSessionRequest {
  pricingPlanId: string;
  email: string;
  userId: string;
  attribution?: CheckoutAttribution;
}

export interface CreateCheckoutSessionResponse {
  data: {
    url: string;
    sessionId: string;
  };
}

// 🎯 Widget System API Types
export interface EnhancePromptRequest {
  user_id: string;
  chat_id: string;
  base_prompt: string;
  eachlabs_model: string;
  llm_model: string;
  task: string;
}

export interface EnhancePromptResponse {
  status: string;
  input: string;
  result: string;
  price: number;
  llm_model_info: {
    llm_model: string;
    input_token: number;
    output_token: number;
  };
  runtime: number;
  task: string;
}

export interface ImageGenerationRequest {
  user_id: string;
  chat_id: string;
  eachlabs_model_name: string; // model yerine eachlabs_model_name kullan
  prompt: string;
  task: string; // yeni field
}

export interface ImageGenerationResponse {
  response: {
    prompt: string;
    status: string;
    result: string; // image URL
    price: number;
  };
  model_info: {
    eachlabs_model_info: {
      model: string;
      version: string;
      input: {
        prompt: string;
        aspect_ratio?: string;
        output_format?: string;
        output_quality?: number;
      };
      webhook_url: string;
    };
  };
  task: string;
  runtime: number;
}

// 🎯 Add Message API Types
export interface AddMessageRequest {
  chatId: string;
  userId: string;
  price: number;
  modelInfo: {
    modelName: string;
    version: string;
    input: {
      prompt?: string;
      aspect_ratio?: string;
      output_format?: string;
      output_quality?: number;
    };
    webhookUrl: string;
  };
  task: string;
  runtime: number;
  prompt: Array<{
    type: "text" | "image";
    content: string;
  }>;
  response: Array<{
    type: "text" | "image" | "card";
    content?: string;
    isCard?: boolean;
    cardContent?: Array<{
      title: string;
      description: string;
      type: string;
      file: string;
      price: number;
    }>;
  }>;
}

export interface AddMessageResponse {
  success: boolean;
  message: string;
  data: {
    message: BackendChatMessage;
  };
}

export interface CheckoutSessionStatusResponse {
  sessionId: string;
  status: "complete" | "expired" | "open";
  payment_status: "no_payment_required" | "paid" | "unpaid";
  subscription: object | null;
  customer: object | null;
  metadata: {
    userId?: string;
    pricingPlanId?: string;
    planName?: string;
  } | null;
  /** Tek seferlik kredi alımı için `payment`, abonelik için `subscription`. */
  mode: string;
  /** Tahsil edilen tutar, para biriminin en küçük biriminde. */
  amount_total: number | null;
  /**
   * Tahsil edilen para birimi — planın ekranda gösterildiği para birimi DEĞİL.
   * Fiyatlar bölgeye göre çevrilerek gösteriliyor ama tahsilat USD yapılıyor.
   */
  currency: string | null;
  /** Yalnızca `payment` modundaki oturumda dolu; abonelikte her zaman null. */
  payment_intent: string | null;
  /** Abonelik satın almasının tekilleştirme anahtarı. */
  invoice: string | null;
}

// 🛒 Shop/Cart API Types
export interface VendorBrief {
  id: string;
  name: string;
  logo: string;
  slug: string;
}

export interface CartItem {
  _id: string;
  productId: string;
  title: string;
  description: string;
  type: string;
  file: string;
  price: number;
  quantity: number;
  userId: string;
  isDeleted: boolean;
  isSelected?: boolean;
  createdAt: string;
  updatedAt: string;
  vendor?: VendorBrief;
  metadata?: {
    size?: string;
    color?: string;
    brand?: string;
    originalPrice?: number | string;
    discountPct?: number;
    [key: string]: any;
  };
}

// 🛒 Yeni Add to Cart Request (Basitleştirilmiş)
export interface AddToCartRequest {
  offerId: string;
  quantity: number;
}

export interface AddToCartResponse {
  success: boolean;
  message: string;
  data: CartItem;
}

export interface UpdateCartQuantityRequest {
  quantity: number;
}

export interface UpdateCartQuantityResponse {
  success: boolean;
  message: string;
  data: CartItem;
}

export interface UpdateCartSelectionRequest {
  cartItemIds: string[];
  isSelected: boolean;
}

export interface UpdateCartSelectionResponse {
  success: boolean;
  message: string;
}

export interface GetAllCartParams {
  isDeleted?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface GetAllCartMeta {
  page: number;
  limit: number;
  total: number;
  totalPage: number;
}

export interface GetAllCartResponse {
  success: boolean;
  message: string;
  data: {
    result: CartItem[];
    meta: GetAllCartMeta;
  };
}

export interface DeleteFromCartResponse {
  success: boolean;
  message: string;
  data?: {
    _id: string;
    isDeleted: boolean;
  };
}

// 🛒 NEW Cart API - Hierarchical Structure (Merchants + Items + Calculations)

// Product Image in Cart
export interface CartProductImage {
  _id: string;
  productId: string;
  imageUrl: string;
  isPrimary: boolean;
  position: number;
}

// Cart Offer (Product Details with Variants)
export interface CartOffer {
  _id: string;
  offerId: string;
  /** Ürün detay sayfası için kullanılacak gerçek product Mongo ID */
  _product?: string;
  productId: string;
  productSellerId: string;
  sellerId: string;
  url: string;

  // Variant Axes
  axis1AttrId?: string;
  axis2AttrId?: string;
  a1ValueId?: string;
  a2ValueId?: string;
  axis1Name?: string; // "Dahili Hafıza", "Renk"
  axis2Name?: string; // "Beden", "Renk"
  a1Value?: string; // "512 GB", "Siyah"
  a2Value?: string; // "XL", "M"

  // Seller Info
  sellerName: string;
  sellerSeoName: string;
  sellerLogoUrl?: string;
  sellerSeoUrl: string;

  // Product Info
  productName: string;
  name: string;
  brandId: string;
  brandName: string;
  brandSeoName: string;
  familyId: string;
  code: string;
  categoryId: string;

  // Merchant Info
  merchantId: string;
  merchantName: string;
  merchantSeoName: string;

  // Pricing
  listPrice: number;
  salePrice: number;
  campaignPrice: number | null;
  listCurrency: string;
  saleCurrency: string;
  campaignCurrency: string | null;

  // Stock
  stockCount: number;
  inStock: boolean;

  // Images (Backend'den gelecek)
  images?: CartProductImage[];

  // Optional
  categoryTags?: string[];
  productRating?: number;
  productCommentCount?: number;
}

// Cart Merchant Item
export interface CartMerchantItem {
  _id: string;
  offerId: string;
  quantity: number;
  offer: CartOffer;
  isSelected?: boolean;
}

// Merchant Calculation
export interface MerchantCalculation {
  subTotal: number;
  cargo: number;
  total: number;
  cashback?: number;
}

// Cart Merchant (Vendor Group)
export interface CartMerchant {
  merchantId: string;
  merchantName: string;
  merchantSeoName: string;
  items: CartMerchantItem[];
  calculation: MerchantCalculation;
}

// Global Cart Calculation
export interface GlobalCartCalculation {
  subTotal: number;
  cargo: number;
  total: number;
  cashback?: number;
}

// 🔄 Cart Change - Sepet otomatik güncelleme bildirimi
export interface CartChange {
  type: 'removed' | 'replaced';
  oldOfferId: string;
  newOfferId?: string;
  productName: string;
  reason: 'out_of_stock' | 'insufficient_stock' | 'unavailable';
}

// Get Cart Response (New Hierarchical Structure)
export interface GetCartResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    merchants: CartMerchant[];
    calculation: GlobalCartCalculation;
    changes?: CartChange[];
  };
}

// 🛍️ E-commerce Brand Types
export interface BrandDetail {
  _id: string;
  name: string;
  logo: string;
  socialLinks: Array<{ platform: string; url: string }>;
  createdAt: string;
  updatedAt: string;
  __v: number;
  brandId: number;
  slug: string;
}

export interface GetBrandByBrandIdResponse {
  success: boolean;
  message: string;
  data: BrandDetail;
}

// 🧾 Checkout - Card Info by BIN
export interface CardInfoPayload {
  binNumber: string; // first 8 digits
}

export interface CardInfoDetail {
  binNumber: string;
  cardType: string; // e.g. "CREDIT_CARD"
  cardAssociation: string; // e.g. "VISA"
  cardFamily: string; // e.g. "Bonus"
  bankName: string;
  bankCode: number;
  commercial: boolean;
}

export interface InstallmentOption {
  installmentNumber: number;
  installmentPrice: number;
  totalPrice: number;
}

export interface CardInfoResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    cardInfo: CardInfoDetail;
    installments: InstallmentOption[];
  };
}

// 🧾 Checkout - Complete Order
export interface CheckoutCardPayload {
  cardHolderName: string;
  cardNumber: string;
  expireYear: string; // YYYY format (e.g., "2025")
  expireMonth: string; // MM format (e.g., "12")
  cvc: string;
  saveCard?: boolean; // Kartı backend'e kaydet
  alias?: string; // Kart takma adı (saveCard: true ise zorunlu)
}

// Saklı kart (backend'den gelen)
export interface SavedCard {
  binNumber: string;
  lastFourDigits: string;
  cardToken: string;
  cardAlias?: string;
  cardType?: string;
  cardAssociation?: string;
  cardBrand?: string;
  cardBankName?: string;
  cardExpiryStatus: string;
}

export interface SavedCardsResponse {
  data: {
    cards: SavedCard[];
  };
  statusCode: number;
  success: boolean;
  message: string;
}

// 💳 Kart Ekleme (POST /cards) — SaveCardDto. CVC yok; alias isteğe bağlı.
export interface AddCardPayload {
  cardHolderName: string;
  cardNumber: string;
  expireYear: string; // YYYY format (e.g., "2026") — checkout ile aynı, bkz. getExpiryParts
  expireMonth: string; // MM format (e.g., "12")
  alias?: string;
}

export interface CheckoutAddressPayload {
  label: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface CheckoutAgreementsAddressPayload extends CheckoutAddressPayload {
  cityId?: number;
  countryId?: number;
  phone?: string;
  quarter?: string;
  quarterId?: string;
  townId?: number;
}

export interface CheckoutAgreementsRequest {
  shippingAddress: CheckoutAgreementsAddressPayload;
  region: string;
}

export interface CheckoutAgreementItem {
  sellerId: string;
  sellerName: string;
  merchantId: string;
  merchantMongoId: string;
  html: string;
  version: string;
  resolvedLocale: string;
}

export interface CheckoutAgreementTypeGroup {
  agreementName: string;
  agreementTitle: string;
  agreements: CheckoutAgreementItem[];
}

export interface CheckoutAgreementsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    agreementTypeGroups: CheckoutAgreementTypeGroup[];
  };
}

export interface CheckoutRequest {
  // Yeni kart ile ödeme
  card?: CheckoutCardPayload;
  // Saklı kart ile ödeme (card yerine cardToken gönderilir)
  cardToken?: string;
  installment: number;
  shippingAddress: CheckoutAddressPayload;
  billingAddress: CheckoutAddressPayload;
  // Hemen Al akışı: sepete eklemeden direkt tek ürün satın alma
  buyNow?: {
    offerId: string;
    quantity: number;
  };
}

export interface CheckoutResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    status: string; // "success", "PENDING", "requires_action", "failed", "FAILED"
    orderGroupId?: string;
    htmlContent?: string; // 3DS HTML content
    errorMessage?: string;
  };
}

// Site Config Types
export interface LLMCategoryIcon {
  type: string; // e.g. "font"
  family?: string; // e.g. "Lucide"
  name: string; // e.g. "image"
}

export interface LLMCategoryLabel {
  en: string;
  tr: string;
  de: string;
  es: string;
  fr: string;
  it?: string;
  ar?: string;
}

export interface LLMCategory {
  _id: string;
  icon: LLMCategoryIcon;
  name: string; // e.g. "image-generation"
  label: LLMCategoryLabel;
  status?: string;
  modelCount?: number;
  hasSubcategories?: boolean;
  /**
   * Admin panelden yönetilen görüntülenme sırası: küçük sayı önce gelir.
   * Alanı göndermeyen eski API'de undefined kalır, o yüzden opsiyonel.
   */
  order?: number;
  __v?: number;
}

export interface LLMBrandThumbnail {
  type: string;
  size: number;
  folder: string;
  fileUrl: string;
}

export interface LLMBrand {
  _id: string;
  title: string; // plain, non-localized brand name
  thumbnail?: LLMBrandThumbnail; // logo image (use getFileUrl on fileUrl)
  llmCategories: LLMCategory[]; // full nested category objects
  /**
   * Admin panelden yönetilen görüntülenme sırası: küçük sayı önce gelir.
   * Alanı göndermeyen eski API'de undefined kalır, o yüzden opsiyonel.
   */
  order?: number;
  __v?: number;
}

export interface LLMCategorySubcategory {
  _id: string;
  parent: {
    icon: LLMCategoryIcon;
    name: string;
    label: LLMCategoryLabel;
    _id: string;
  };
  name: string;
  label: LLMCategoryLabel;
}

export interface LLMCategorySubcategoriesResponse {
  data: LLMCategorySubcategory[];
  statusCode: number;
  success: boolean;
  message: string;
}

export interface SiteConfigData {
  llmCategories: LLMCategory[];
  brands?: LLMBrand[]; // Brands with their nested categories
  agreements?: Array<{
    name: string;
    version: string;
  }>;
  defaultModel?: LLMModel; // Full model object from backend
  /**
   * Whether this deployment can issue partner loyalty points. Optional so an
   * older API that doesn't send it reads as off — see useCosmopointsEnabled.
   */
  cosmopointsEnabled?: boolean;
}

export interface SiteConfigResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: SiteConfigData;
}

// Billing History Types
export interface BillingUsage {
  tokens: {
    subscription: number;
    oneTime: number;
    _id: string;
  };
  maxTokens: {
    subscription: number;
    oneTime: number;
    _id: string;
  };
}

export interface BillingInvoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  downloadUrl: string;
}

export interface BillingPaymentMethod {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  email: string;
  id: string;
}

export interface BillingSubscription {
  _id: string;
  userId: string;
  pricingPlanId: string;
  stripeCustomerId: string;
  status: "active" | "canceled" | "expired" | "pending";
  stripeStatus: "paid" | "unpaid" | "canceled";
  amountPaid: number;
  stripeSubscriptionId: string;
  currency: string;
  expiresAt: string;
  resetsAt?: string;
  isRecurring: boolean;
  paymentDate: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  cancelReason?: string;
}

export interface BillingHistoryData {
  usage: BillingUsage;
  invoices: BillingInvoice[];
  paymentMethod: BillingPaymentMethod | null;
  pricingPlan: PricingPlan | null;
  subscription?: BillingSubscription | null;
}

export interface BillingHistoryResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: BillingHistoryData;
}

// 📚 Library API Types
export interface LibraryFile {
  type: string;
  size: number;
  folder: string;
  fileUrl: string;
}

export interface LibraryItem {
  _id: string;
  userId: string;
  title: string;
  type: string;
  file: LibraryFile;
  messageId?: {
    _id: string;
    isPublic: boolean;
    model?: {
      title?: string;
      name?: string;
      thumbnail_url?: string;
    } | null;
  };
  prompt?: string;
  categories?: string[];
  tags?: string[];
  isInput?: boolean;
  isWardrobe?: boolean;
  isFavorite?: boolean;
  folderIds?: string[];
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface LibraryFolder {
  _id: string;
  name: string;
  itemCount: number;
  createdAt: string;
}

export interface LibraryStats {
  total: number;
  favorites: number;
  byType: Partial<Record<string, number>>;
}

export interface BulkFavoriteLibraryRequest {
  ids: string[];
  isFavorite: boolean;
}

export interface BulkFolderLibraryRequest {
  ids: string[];
  folderId: string;
  action: "add" | "remove";
}

export interface LibraryMeta {
  limit: number;
  page: number;
  total: number;
  totalPage: number;
}

export interface LibraryResponse {
  result: LibraryItem[];
  meta: LibraryMeta;
}

export interface LibraryListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  title?: string;
  isInput?: boolean;
  isWardrobe?: boolean;
  tags?: string;
  isFavorite?: boolean;
  folderId?: string;
}

// Library Delete Types
export interface BulkDeleteLibraryRequest {
  ids: string[];
}

export interface BulkDeleteLibraryResponse {
  data: {
    deletedCount: number;
    failedIds: string[];
  };
  statusCode: number;
  success: boolean;
  message: string;
}

// Cancel Subscription Types
export interface CancelSubscriptionRequest {
  reason: string;
}

export interface CancelSubscriptionResponse {
  data: {};
  statusCode: number;
  success: boolean;
  message: string;
}

// Customer Portal Types
export interface CustomerPortalRequest {
  type: "subscription_update_confirm" | "payment_method_update";
  subscriptionId?: string;
  targetPricingPlanId?: string;
}

export interface CustomerPortalResponse {
  data: {
    url: string;
  };
  statusCode: number;
  success: boolean;
  message: string;
}

// Compare Pricing Plan Types
export type ComparePricingPlanChangeType = "no_change" | "upgrade" | "downgrade";

export interface ComparePricingPlanResponse {
  data: {
    type: ComparePricingPlanChangeType;
  };
  statusCode: number;
  success: boolean;
  message: string;
}

// Active Coupon Types
export type CouponArea = "token_ended";
export type CouponDiscountType = "percent" | "amount";

export interface ActiveCoupon {
  code: string;
  area: CouponArea;
  title?: string;
  description?: string;
  discountType?: CouponDiscountType;
  discountValue?: number;
  currency?: string;
  expiresAt?: string;
}

export interface GetActiveCouponResponse {
  data: ActiveCoupon | null;
  statusCode: number;
  success: boolean;
  message?: string;
}

// Login History Types
export interface LoginHistoryItem {
  _id: string;
  userId: string;
  ipAddress: string;
  device: string;
  location: string;
  type: "desktop" | "mobile" | "tablet";
  createdAt: string;
  updatedAt: string;
  __v: number;
  id: string;
}

export interface LoginHistoryMeta {
  limit: number;
  page: number;
  total: number;
  totalPage: number;
}

export interface LoginHistoryResponse {
  result: LoginHistoryItem[];
  meta: LoginHistoryMeta;
}

export interface LoginHistoryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
}

// 👤 Image Upload Types
export interface UploadImageRequest {
  file: File;
}

export interface UploadImageResponse {
  data: {
    type: string;
    size: number;
    folder: string;
    fileUrl: string;
  };
  statusCode: number;
  success: boolean;
  message: string;
}

// 📊 Usage Analytics Types
export interface UsageAnalyticsModel {
  modelId: string;
  modelTitle: string;
  tokens: number;
  cost: number;
}

export interface UsageAnalyticsSeriesByDate {
  date: string;
  models: UsageAnalyticsModel[];
}

export interface UsageAnalyticsInsights {
  mostUsedModel: {
    modelId: string;
    modelTitle: string;
    tokens: number;
  };
  averageDailyUsage: number;
  highUsageTrendDetected: boolean;
}

export interface UsageAnalyticsData {
  days: string[];
  seriesByDate: UsageAnalyticsSeriesByDate[];
  insights: UsageAnalyticsInsights;
}

export interface UsageAnalyticsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: UsageAnalyticsData;
}

// 📊 Token Usage History by Model Types
export interface TokenUsageHistoryModel {
  modelId: string;
  modelTitle: string;
  totalCost: number;
  usageCount: number;
  unitPrice?: number; // Backend ekleyecek; şimdilik opsiyonel
  totalTokens: number;
}

export interface TokenUsageHistoryMeta {
  limit: number;
  page: number;
  total: number;
  totalPage: number;
}

export interface TokenUsageHistoryData {
  result: TokenUsageHistoryModel[];
  meta: TokenUsageHistoryMeta;
}

export interface TokenUsageHistoryResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: TokenUsageHistoryData;
}

// 📊 Token Usage History Query Params
export interface TokenUsageHistoryParams {
  page?: number;
  limit?: number;
  sortBy?: string; // örn: "unitPrice" veya "-unitPrice"
  search?: string; // serbest metin arama
}

// 📟 Token Usage Run History Types
export interface TokenUsageRunHistoryModelInfo {
  _id: string;
  title: string;
}

export interface TokenUsageRunHistoryLibraryItem {
  isWardrobe: boolean;
  _id: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  title: string;
  type: string;
  file: LibraryFile;
  isInput: boolean;
}

export interface TokenUsageRunHistoryModel {
  _id: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  messageId: string;
  conversationId: string;
  modelId: TokenUsageRunHistoryModelInfo | null;
  libraryItemIds: TokenUsageRunHistoryLibraryItem[] | null;
  tokenAmount: number;
  cost: number;
  summary: string;
  status: string;
  runTime: number;
  type:
    | "generation"
    | "chat"
    | "enhancement"
    | "translation"
    | "onboarding"
    | null;
}

export interface TokenUsageRunHistoryMeta {
  limit: number;
  page: number;
  total: number;
  totalPage: number;
}

export interface TokenUsageRunHistoryData {
  result: TokenUsageRunHistoryModel[];
  meta: TokenUsageRunHistoryMeta;
}

export interface TokenUsageRunHistoryResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: TokenUsageRunHistoryData;
}

export interface TokenUsageRunHistoryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
}

// 🔔 Notification Types
export interface NotificationSettings {
  account: NotificationCategory;
  marketing: NotificationCategory;
  security: NotificationCategory;
  _id: string;
}

export interface NotificationCategory {
  push: boolean;
  email: boolean;
  sms: boolean;
}

export interface UpdateNotificationRequest {
  notifications: NotificationSettings;
}

// LLM Model Favorites Types
export interface LLMModelFavorite {
  name: string;
  thumbnail_url: string;
  _id: string;
  title: string;
  brand: {
    title: string;
    _id: string;
  };
  description: {
    en: string;
    tr: string;
    de: string;
    es: string;
    fr: string;
    it?: string;
    ar: string;
  };
}

export interface LLMModelFavoritesResponse {
  data: LLMModelFavorite[];
  statusCode: number;
  success: boolean;
  message: string;
}

export interface AddFavoriteRequest {
  modelId: string;
}

export interface AddFavoriteResponse {
  data: {};
  statusCode: number;
  success: boolean;
  message: string;
}

// LLM Model Comments Types
export interface LLMModelComment {
  _id: string;
  userId: User;
  modelId: string;
  comment: string;
  rating?: number;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface LLMModelCommentsResponse {
  data: {
    result: LLMModelComment[]; // Yeni format: result array'i
    hasCommented: boolean;
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    // Geriye dönük uyumluluk için (deprecated)
    comments?: LLMModelComment[];
  };
  statusCode: number;
  success: boolean;
  message: string;
}

export interface AddCommentRequest {
  comment: string;
  modelId: string;
}

export interface AddCommentResponse {
  data: {};
  statusCode: number;
  success: boolean;
  message: string;
}

// LLM Model Rating Types
export interface LLMModelRatingResponse {
  data: {
    averageRating: number;
  };
  statusCode: number;
  success: boolean;
  message: string;
}

export interface AddRatingRequest {
  rating: number;
  modelId: string;
}

export interface AddRatingResponse {
  data: {};
  statusCode: number;
  success: boolean;
  message: string;
}

// 📝 Agreement Types
export interface AgreementConfirmation {
  agreement: {
    name: string;
    title: Record<string, string>;
    content: Record<string, string>;
    version: string;
    previousVersions?: Array<{ version: string }>;
    createdAt: string;
    updatedAt: string;
    _id: string;
  };
  version: string;
  confirmedAt: string;
}

export interface AgreementResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    _id: string;
    name: string;
    title: Record<string, string>;
    content: Record<string, string>;
    version: string;
    createdAt: string;
    updatedAt: string;
    previousVersions?: Array<{ version: string }>;
  };
}

export interface ConfirmAgreementRequest {
  agreementName: string;
}

export interface ConfirmAgreementResponse {
  data: AgreementConfirmation[];
  statusCode: number;
  success: boolean;
  message: string;
}

// 🎯 Prompt Enhancement API Types
export interface PromptEnhanceRequest {
  prompt: string;
}

export interface PromptEnhanceResponse {
  enhancedPrompt: string;
  originalPrompt: string;
}

// 🌍 Prompt Translation API Types
export interface PromptTranslateRequest {
  prompt: string;
}

export interface PromptTranslateResponse {
  translatedPrompt: string;
  originalPrompt: string;
}

// 🎙️ Dictation (Speech-to-Text) API Types — POST /api/v1/transcribe/text
export interface TranscribeTextData {
  text: string;
  language: string;
  durationMs: number;
}

export interface TranscribeAudioResponse {
  statusCode: number;
  success: boolean;
  message?: string;
  data: TranscribeTextData;
}

// 🧮 Creative Generation Estimate Cost API Types
export interface CreativeGenerationEstimateCostRequest {
  model: string;
  inputs: Record<string, unknown>;
  providerId: string;
}

export interface CreativeGenerationEstimateCostData {
  unit_price: number;
  unit: string;
  currency: string;
  estimated_units: number;
  estimated_cost_usd: number;
  estimated_credits: number;
  source: string;
}

export interface CreativeGenerationEstimateCostResponse {
  statusCode: number;
  success: boolean;
  data: CreativeGenerationEstimateCostData;
}

// 🧾 Checkout - Shipping Calculation Types
export interface ShippingCalculationPayload {
  addressId: string;
  vendorIds: string[];
}

export interface VendorShipping {
  vendorId: string;
  shippingCost: number;
  estimatedDelivery: string;
}

export interface ShippingCostResponse {
  success: boolean;
  data: {
    vendorShipping: VendorShipping[];
  };
}

// 🧾 Checkout - Order Creation Types
export interface OrderPayload {
  deliveryAddressId: string;
  billingAddressId?: string;
  items: Array<{
    productId: string;
    quantity: number;
    price: number;
  }>;
  shippingCosts: Record<string, number>;
  subtotal: number;
  total: number;
  discount?: number;
  couponCode?: string;
}

export interface OrderResponse {
  success: boolean;
  message: string;
  data: {
    orderId: string;
    orderNumber: string;
    status: string;
    createdAt: string;
  };
}

// 🧾 Orders API Types
export interface OrderItemImage {
  imageUrl: string;
  productId: string;
  isPrimary: boolean;
  position: number;
}

export interface OrderItem {
  code: string;
  name: string;
  price: number;
  quantity: number;
  amount: number;
  product: string | object;
  offer: string | object;
  productId: string;
  offerId: string;
  axis1AttrId?: string;
  axis2AttrId?: string;
  a1ValueId?: string;
  a2ValueId?: string;
  axis1Name?: string;
  axis2Name?: string;
  a1Value?: string;
  a2Value?: string;
  images?: OrderItemImage[];
}

export interface OrderAddress {
  label: string;
  firstName?: string;
  lastName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// Checkout sipariş işleme durumu (polling için)
export enum OrderProcessingStatus {
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface CheckoutStatusResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    status: OrderProcessingStatus;
    orderGroupId?: string;
    failureReason?: OrderFailureReason;
  };
}

export const OrderGroupStatuses = [
  'active',    // Devam ediyor
  'completed', // Tamamlandı
  'canceled',  // İptal edildi
  'refunded',  // İade edildi
] as const;

export type OrderGroupStatus = typeof OrderGroupStatuses[number];

export const OrderStatuses = [
  'pending',    // Ödeme bekleniyor
  'paid',       // Ödeme alındı
  'processing', // İşleniyor
  'shipped',    // Kargoya verildi
  'delivered',  // Teslim edildi
  'canceled',   // İptal edildi
  'refunded',   // İade edildi
  'failed',     // Ödeme başarısız
] as const;

export type OrderStatus = typeof OrderStatuses[number];

export interface OrderFailureReason {
  code: string;
  message: string;
}

export interface Order {
  _id: string;
  userId: string | object;
  orderId: string;
  orderGroup?: string; // Order group reference
  orderGroupId: string;
  externalOrderId: string;
  merchantId: string;
  merchantName: string;
  merchantSeoName: string;
  merchantLogoUrl?: string;
  merchantUrl?: string;
  items: OrderItem[];
  totalAmount: number;
  shippingCost?: number;
  cashbackAmount?: number;
  totalVatAmount?: number;
  installment?: number;
  currency: string;
  status: string | object;
  failureReason?: OrderFailureReason;
  trackingNumber?: string;
  trackingUrl?: string;
  invoiceLink?: string;
  orderLink?: string;
  orderProvider?: string;
  recipientName?: string;
  errorMessage?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

// Order Group Detail (from getOrderGroup endpoint)
export interface OrderGroupDetail {
  _id: string;
  userId: string;
  orderGroupId: string;
  totalAmount: number;
  subTotal?: number;
  shippingCost?: number;
  cashbackAmount?: number;
  totalVatAmount?: number;
  installment?: number;
  currency: string;
  orderCount: number;
  status: string;
  shippingAddress: OrderAddress;
  billingAddress: OrderAddress;
  paymentMethod: string;
  cardAssociation?: string;
  lastDigits?: string;
  orderProvider: string;
  recipientName?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface OrderGroupResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    orders: Order[];
    detail: OrderGroupDetail;
  };
}

// 🧾 Orders List API Types
export interface OrderListItem {
  _id: string;
  orderGroupId: string;
  orderCount: number;
  productCount: number;
  totalAmount: number;
  currency: string;
  status: string;
  images?: string[]; // Array of image URLs
  createdAt: string;
  recipientName?: string; // Recipient name from shipping address
}

export interface OrderListParams {
  page?: number;
  limit?: number;
}

export interface OrderListResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: OrderListItem[];
  total: number;
  page: number;
  limit: number;
}

// 🛍️ E-commerce Category API Types
export interface CategoryLabel {
  en: string;
  tr: string;
  de: string;
  es: string;
  fr: string;
  it?: string;
  ar: string;
}

export interface CategoryImage {
  type: string;
  size: number;
  folder: string;
  fileUrl: string;
}

export interface MainCategory {
  _id: string;
  name: string;
  label: CategoryLabel;
  image: CategoryImage;
}

export interface MainCategoriesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: MainCategory[];
}

export interface SubCategory {
  categoryId: string;
  name: string;
}

export interface SubCategoriesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: SubCategory[];
}

// 🛍️ E-commerce Seller API Types
export interface PopularSeller {
  _id: string;
  sellerId: string;
  sellerLogoUrl: string;
  sellerName: string;
  sellerRating: number | null;
  sellerSeoName: string;
}

export interface PopularSellersResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: PopularSeller[];
}

// 🛍️ E-commerce Featured Products API Types
export interface HarmonySelectionSearch {
  en: string;
  tr: string;
  de: string;
  es: string;
  fr: string;
  it?: string;
  ar: string;
}

export interface HarmonySelectionImage {
  type: string;
  size: number;
  folder: string;
  fileUrl: string;
}

export interface HarmonySelection {
  _id: string;
  id: string;
  title: string;
  search: HarmonySelectionSearch;
  categories: string[]; // Category ID array
  image: HarmonySelectionImage;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface FeaturedProductsData {
  featuredProductIds: string[];
  harmonySelections: HarmonySelection[];
}

export interface FeaturedProductsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: FeaturedProductsData;
}

// 🔗 Chat Share Link API Types
export interface GetShareLinkRequest {
  conversationId: string;
}

export interface GetShareLinkData {
  link: string;
}

export interface GetShareLinkResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: GetShareLinkData;
}

export interface RemoveShareTokenResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: Conversation;
}

// 🎨 Category Preferences Types
export interface CategoryPreferenceAttribute {
  attributeValueId: string;
  value: string;
  categoryId: string;
}

export interface CategoryPreferenceType {
  type: string; // women_tops, men_shoes, vb.
  attributes: CategoryPreferenceAttribute[];
}

export interface CategoryPreferencesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: CategoryPreferenceType[];
}

// 🎨 User Preferences Types
export interface UserPreferencesData {
  preferences: Record<string, CategoryPreferenceAttribute[]>; // type -> attributes
  brandPreferences: Array<{ id: string; name: string }>;
}

// Alias for backward compatibility
export type UserPreferences = UserPreferencesData;

export interface UserPreferencesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: UserPreferencesData;
}

export interface UpdatePreferencesRequest {
  preferences: Record<string, CategoryPreferenceAttribute[]>;
  brandPreferences: Array<{ id: string; name: string }>;
}

// 🎁 Rewards Types
// GET /users/rewards → current one-time token balance + reward credit balance
export interface RewardBalances {
  oneTime: number; // current one-time token balance
  credits: number; // current reward credit balance
}

export interface RewardsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: RewardBalances;
}

// GET /users/rewards/history → paginated reward credit rows
export interface RewardHistoryItem {
  _id: string;
  amount: number; // credits earned (+) / converted (-)
  balance: number; // running reward credit balance after this row
  orderGroupId?: string;
  source?: string;
  note?: string;
  createdAt: string;
  updatedAt: string;
  user?: unknown;
}

export interface RewardHistoryMeta {
  limit: number;
  page: number;
  total: number;
  totalPage: number;
}

export interface RewardHistoryData {
  result: RewardHistoryItem[];
  meta: RewardHistoryMeta;
}

export interface RewardHistoryResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: RewardHistoryData;
}

// 🏪 Checkout Approvals Types
export interface MerchantApprovalContact {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  country: string;
}

export interface MerchantApproval {
  merchantId: string;
  merchantName: string;
  status: boolean;
  cargo: string;
  refund: string;
  cancellation: string;
  contact: MerchantApprovalContact;
}

export interface CheckoutApprovalsResponse {
  statusCode: number;
  success: boolean;
  data: MerchantApproval[];
}

export interface SubmitApprovalItem {
  merchantId: string;
  status: true;
}

export interface SubmitCheckoutApprovalsRequest {
  approvals: SubmitApprovalItem[];
}

export interface RewardHistoryParams {
  page?: number;
  limit?: number;
}

// 🎁 Reward conversion
// Where converted credits go: Harmony one-time tokens, or points in a linked
// partner loyalty program.
export type RewardConversionTarget = "tokens" | "cosmopoints";

// POST /users/rewards/convert
// Omitting `target` keeps the original 1:1 credits → one-time tokens behaviour.
export interface ConvertRewardsRequest {
  amount: number;
  target?: RewardConversionTarget;
  connectionId?: string;
}

export interface ConvertRewardsData {
  oneTime: number;
  credits: number;
  converted: number;
  target: RewardConversionTarget;
  // Present only when target === "cosmopoints"
  totalPoints?: number;
  programId?: string;
  programName?: string;
  transactionId?: string;
}

export interface ConvertRewardsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data?: ConvertRewardsData;
}

// POST /users/rewards/convert/preview → quotes both destinations at once
export interface PreviewConvertRewardsRequest {
  amount: number;
  connectionId?: string;
}

export interface RewardConversionPointsQuote {
  connectionId: string;
  programId: string;
  programName?: string;
  valueUsd: number;
  totalPoints: number;
}

export interface PreviewConvertRewardsData {
  amount: number;
  tokens: number;
  /** Null when points are unavailable; see `unavailableReason`. */
  cosmopoints: RewardConversionPointsQuote | null;
  /** Error code, e.g. "no_connection" | "not_configured" | "min_value_not_reached". */
  unavailableReason?: string;
}

export interface PreviewConvertRewardsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: PreviewConvertRewardsData;
}

// 🛫 Cosmopoints partner loyalty programs
export interface CosmopointsProgram {
  id: string;
  name: string;
  logo?: string;
  currency?: string;
  /**
   * Which destination field the program needs when linking an account.
   * The API normalises this to a name list, but the upstream vendor sends a
   * `{ field: type }` map — hence the union.
   */
  requiredFields?: string[] | Record<string, unknown>;
}

export interface CosmopointsProgramListResponse {
  statusCode: number;
  success: boolean;
  data: { programs: CosmopointsProgram[] };
}

export interface CosmopointsConnection {
  _id: string;
  programId: string;
  programName?: string;
  programLogo?: string;
  programCurrency?: string;
  destinationAccount: string;
  destinationField: "destinationUserId" | "destinationUserEmail";
  isDefault: boolean;
  /** Lifetime points Harmony has issued into this program. */
  pointsEarned: number;
  validatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CosmopointsConnectionListResponse {
  statusCode: number;
  success: boolean;
  data: CosmopointsConnection[];
}

export interface CosmopointsConnectionResponse {
  statusCode: number;
  success: boolean;
  data: CosmopointsConnection;
}

export interface CreateCosmopointsConnectionRequest {
  programId: string;
  destinationAccount: string;
  partnerCode?: string;
  makeDefault?: boolean;
}
